import linkIcon from '@disy/cadenza-icons/link.svg';
import h from 'hyperscript';
import groupBy from 'lodash-es/groupBy';

import { closePopup, setupPopupButton } from 'ui/popup/popup';
import { SelectList } from 'ui/select-list/select-list';
import { createIconButton } from 'ui/button/button';

import { isFeatureAvailable } from 'cadenza/features';
import { icon } from 'cadenza/utils/icon/icon';
import { sortProcessings } from 'cadenza/processes/sort-processings';
import { createPreviewImageUrl } from 'cadenza/api-client/processing-api';
import {
  checkResultLimits,
  getProcessingUrl,
  getTableResultAsObjectSelectionPostMessageEvent
} from 'cadenza/api-client/processing-chain-api';
import { uniqueId } from 'cadenza/utils/unique-id';
import { on } from 'cadenza/utils/event-util';
import { doesUserTryToOpenInNewTabOrNewWindow } from 'cadenza/utils/input-events';
import { logger } from 'cadenza/utils/logging';
import { PermalinkView } from 'cadenza/permalink/permalink-view';
import { showProcessDialog } from 'cadenza/processes/process-dialog';

import './processes-list.css';
import i18n from './messages.properties';

const COMPONENT_NAME = 'd-processes-list';

export function createProcessesList (processings) {
  const groupedProcesses = groupBy(sortProcessings(processings), 'category');
  return h(`.${COMPONENT_NAME}.d-modal--scrollable`, Object.values(groupedProcesses).map(processes => {
    const labelId = uniqueId();
    const selectionList = new SelectList(processes.map(createProcessItem), { ariaLabelledBy: labelId });
    on(selectionList, 'change', (event) => {
      const process = selectionList.selection[0].process;
      executeProcess(process);
      closePopup();
    });
    return h('details.d-panel-section', { open: true }, [
      h('summary', { id: labelId }, processes[0].categoryLabel),
      selectionList
    ]);
  }));
}

export function isCustomTemplatePostMessageProcessing (process) {
  return process.type === 'custom' && process.communicationType === 'POST_MESSAGE';
}

function createProcessItem (process) {
  const name = 'd-process';
  process.url = getProcessingUrl(process);
  const item = h(`a.${name}`, { href: process.url, tabIndex: -1, process }, [
    createProcessPreview(process),
    h(`.${name}--name`, process.name),
    h(`.${name}--description`, shorten(process.description))
  ]);
  on(item, 'click', (event) => {
    if (!doesUserTryToOpenInNewTabOrNewWindow(event)) {
      event.preventDefault();
    }
  });
  if (process.type === 'report' && isFeatureAvailable('EXTENDED_PERMALINK_CREATION')) {
    const permalinkButton = createIconButton(icon(linkIcon), i18n('permalink.button.title'), {
      onpointerdown: (event) => event.preventDefault(), // Prevent default, so that the above listener is not executed
      onclick: (event) => event.preventDefault(), // Prevent default, because the button is nested in a link
      styleClass: `${name}--permalink-indicator`,
      variant: 'borderless'
    });
    setupPopupButton(permalinkButton, () => getPermalinkContent(process), { styleClass: 'd-permalink-menu' });
    item.append(permalinkButton);
  }
  return item;
}

function createProcessPreview ({ repositoryName, previewImage, groupType }) {
  const preview = h('.d-process--preview');
  const previewImageUrl = createPreviewImageUrl(repositoryName, previewImage);
  if (previewImageUrl) {
    preview.style.backgroundImage = `url(${previewImageUrl})`;
  } else {
    preview.classList.add(`type-${groupType}`);
  }
  return preview;
}

function shorten (text, length = 100) {
  if (text && text.length > length) {
    return text.substr(0, length) + '…';
  }
  return text;
}

function getCountExceedsLimitsMessage (res) {
  if (res.currentCount > res.maxCount && res.maxCount !== -1) {
    return 'resultLimitsException.maxResults';
  }
  if (res.currentCount < res.minCount && res.minCount !== -1) {
    return 'resultLimitsException.minResults';
  }
  return 'resultLimitsException.unknown';
}

function executeProcess (process) {
  if (process.hasResultLimits) {
    const progressPromise = new Promise((resolve, reject) => {
      checkResultLimits(process).then(res => {
        if (res.maxCount !== -1 || res.minCount !== -1) {
          reject(new Error(i18n(getCountExceedsLimitsMessage(res), res)));
        } else if (res.generalException) {
          logger.error(`Could not check process result limits: ${res.generalException}`);
          reject(new Error(i18n('resultLimitsException.generalError')));
        } else {
          resolve({ url: process.url });
        }
      }).catch(error => {
        logger.error('Failed to check result limits', error);
        reject(new Error(i18n('resultLimitsException.generalError')));
      }).finally(() => resolve());
    });
    showProcessDialog(process.name, progressPromise);
  } else {
    showProcessDialog(process.name, executeParallelViewsProcess(process));
  }
}

export function executeParallelViewsProcess (process) {
  if (process.type === 'custom') { // Custom Web Application Process
    return executeWebApplicationProcess(process);
  } else {
    return Promise.resolve({ url: process.url });
  }
}

function executeWebApplicationProcess (process) {
  if (process.communicationType === 'POST_MESSAGE') { // WebApp communication via PostMessage
    return getTableResultAsObjectSelectionPostMessageEvent(process)
      .then(postMessageObjectSelectionEvent => {
        const resultAsPostMessage = {
          targetOrigin: process.targetOrigin,
          targetWindowSelector: process.targetWindowSelector,
          eventObject: postMessageObjectSelectionEvent
        };
        return { resultAsPostMessage };
      });
  } else if (process.communicationType === 'URL') { // WebApp communication via URLs
    if (process.resultHandlingConfig.resultHandlingTypeShortName === 'redirect') { // display in iFrame
      // will be handled by the ProcessingChainController
      return Promise.resolve({ url: process.url });
    } else if (process.resultHandlingConfig.resultHandlingTypeShortName === 'popup') { // display as Popup
      const url = process.url;
      const popup = process.resultHandlingConfig;
      return Promise.resolve({ url, popup });
    } else {
      return Promise.reject(new Error('Unsupported custom template processing'));
    }
  } else { // Other communication types are not supported at the moment
    return Promise.reject(new Error('Unsupported custom template processing'));
  }
}

function getPermalinkContent (process) {
  const urlSearchParams = new URLSearchParams(process.url.slice(process.url.indexOf('?')));
  const options = {
    type: 'selector',
    mimetype: null, // mimetype unknown, setting it to null will result in uding default mimetype
    isPermalinkCreationAllowed: true, // permalink icon was drawn only if this was true
    urlSearchParams
  };
  return new PermalinkView(options);
}
